import { useEffect, useState } from 'react';
import { useOrg } from '@/features/team/OrgProvider';
import { ClientPicker } from './ClientPicker';
import { EstablishmentEditor } from './EstablishmentEditor';
import { ConfirmarBorrado } from '@/ui/ConfirmarBorrado';
import { MenuAcciones } from '@/ui/MenuAcciones';
import { EncabezadoPantalla, Seccion } from '@/ui/Sistema';
import { esErrorDeRed, mensajeDeError, useSinConexion } from '@/core/red/conectividad';
import { useBundleOpcional } from '@/core/offline/BundleProvider';
import { RepartoFactura } from '@/ui/RepartoFactura';
import { AvisoFlotante } from '@/ui/AvisoFlotante';
import {
  listParticipants,
  listSplitTemplates,
  type Participant,
  type SplitTemplate,
} from '@/features/works/works';
import { updateClient } from './clients';
import {
  createEstablishment,
  fetchOrganizationSettings,
  listAllClients,
  listEstablishmentsByClient,
  listEstablishments,
  setClientActive,
  type Client,
  type Establishment,
  type OrganizationSettings,
  deleteClient,
  deleteEstablishment,
  fetchClientInvoiceRecipients,
  setClientInvoiceRecipients,
  type DestinatarioFactura,
} from './clients';

export function ClientsPage() {
  const { activeOrg } = useOrg();
  const [clients, setClients] = useState<Client[]>([]);
  // Los campos de cada cliente, para el renglón secundario del listado.
  const [campos, setCampos] = useState<Record<string, Establishment[]>>({});
  const sinServidor = useSinConexion();
  const { bundle } = useBundleOpcional() ?? { bundle: null };
  const [selected, setSelected] = useState<Client | null>(null);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [settings, setSettings] = useState<OrganizationSettings | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  // `null` = nada esperando. Guarda qué se está por borrar, no si hay algo.
  const [borrandoCliente, setBorrandoCliente] = useState<Client | null>(null);
  const [borrandoCampo, setBorrandoCampo] = useState<Establishment | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [destinatarios, setDestinatarios] = useState<DestinatarioFactura[]>([]);
  const [nuevoCampo, setNuevoCampo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const [plantillas, setPlantillas] = useState<SplitTemplate[]>([]);

  const orgId = activeOrg?.organization_id;

  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        const ts = await listSplitTemplates(orgId);
        if (!cancelado) setPlantillas(ts);
      } catch {
        // Sin plantillas el selector queda con la opción de la organización.
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId]);

  const cambiarPlantilla = async (templateId: string | null) => {
    if (!selected) return;
    await updateClient(selected.id, { default_split_template_id: templateId });
    setSelected({ ...selected, default_split_template_id: templateId });
    setRecarga((n) => n + 1);
  };

  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        // Sin servidor no se pregunta: se lee la copia operativa. Preguntar y
        // fallar dejaba la pantalla diciendo «0 en total», que no es cierto —no
        // hay cero clientes, no se pudo consultar— y además tapaba el hecho de
        // que los clientes SÍ estaban en el teléfono.
        if (sinServidor) {
          if (cancelado) return;
          setClients(bundle?.clientes ?? []);
          setCampos(bundle?.establecimientos ?? {});
          setParticipants(bundle?.participantes ?? []);
          setSettings(bundle?.ajustes ?? null);
          return;
        }

        const [lista, config, gente, porCliente] = await Promise.all([
          listAllClients(orgId),
          fetchOrganizationSettings(orgId),
          listParticipants(orgId),
          listEstablishmentsByClient(orgId),
        ]);
        if (cancelado) return;
        setClients(lista);
        setSettings(config);
        setParticipants(gente);
        setCampos(porCliente);
      } catch (e) {
        if (cancelado) return;
        // Un fallo de red no es un error de esta pantalla: es el modo sin
        // conexión, y se atiende mostrando lo que hay guardado.
        if (esErrorDeRed(e)) {
          setClients(bundle?.clientes ?? []);
          setCampos(bundle?.establecimientos ?? {});
          setParticipants(bundle?.participantes ?? []);
          setSettings(bundle?.ajustes ?? null);
        } else {
          setError(mensajeDeError(e));
        }
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, recarga]);

  // Los destinatarios del cliente elegido. Es a nombre de quién se le emite la
  // factura, y no tiene nada que ver con quién factura de la organización ni
  // con el reparto económico.
  // Al cambiar de cliente hay que limpiar lo que se estaba viendo, o por un
  // instante se muestran los destinatarios del anterior sobre el nuevo. Se
  // ajusta DURANTE el render y no en un efecto: en React 19 un `setState`
  // síncrono dentro de un efecto encadena renders.
  const [ultimoCliente, setUltimoCliente] = useState<string | null>(null);
  if ((selected?.id ?? null) !== ultimoCliente) {
    setUltimoCliente(selected?.id ?? null);
    setDestinatarios([]);
  }

  useEffect(() => {
    if (!selected) return;
    let cancelado = false;
    void (async () => {
      try {
        const r = await fetchClientInvoiceRecipients(selected.id);
        if (!cancelado) setDestinatarios(r);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [selected, recarga]);

  useEffect(() => {
    let cancelado = false;
    // El estado se toca siempre después de un await: nunca de forma síncrona
    // dentro del efecto (evita renders en cascada).
    void (async () => {
      try {
        const lista = await (selected ? listEstablishments(selected.id) : Promise.resolve([]));
        if (!cancelado) setEstablishments(lista);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [selected, recarga]);

  if (!activeOrg || !orgId) return null;

  const agregarCampo = async () => {
    if (!selected || nuevoCampo.trim() === '') return;
    setError(null);
    try {
      await createEstablishment({
        organizationId: orgId,
        clientId: selected.id,
        name: nuevoCampo,
      });
      setNuevoCampo('');
      setRecarga((n) => n + 1);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  const guardarDestinatarios = async () => {
    if (!selected) return;
    setError(null);
    try {
      await setClientInvoiceRecipients(selected.id, destinatarios);
      setAviso('Destinatarios guardados');
    } catch (e) {
      // La base rechaza si no suman 100: el mensaje ya lo dice con el número.
      setError(mensajeDeError(e));
    }
  };

  const borrarCliente = async (c: Client) => {
    setError(null);
    try {
      await deleteClient(c.id);
      setBorrandoCliente(null);
      if (selected?.id === c.id) setSelected(null);
      setAviso(`${c.name} eliminado`);
      setRecarga((n) => n + 1);
    } catch (e) {
      // La base contesta cuántos trabajos hay y qué hacer. Se muestra tal cual:
      // «tiene 12 trabajos, borralos primero o desactivalo» es accionable;
      // «no se puede» manda a adivinar.
      setError(mensajeDeError(e));
      setBorrandoCliente(null);
    }
  };

  const borrarCampo = async (e0: Establishment) => {
    setError(null);
    try {
      await deleteEstablishment(e0.id);
      setBorrandoCampo(null);
      setAviso(`${e0.name} eliminado`);
      setRecarga((n) => n + 1);
    } catch (e) {
      setError(mensajeDeError(e));
      setBorrandoCampo(null);
    }
  };

  const alternarActivo = async (c: Client) => {
    setError(null);
    try {
      await setClientActive(c.id, !c.is_active);
      if (selected?.id === c.id) setSelected(null);
      setRecarga((n) => n + 1);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  return (
    <div className="flex flex-col gap-5 p-4" data-testid="clients-page">
      <EncabezadoPantalla titulo="Clientes" nota="Clientes y campos" />

      {/* El buscador arriba y sin envoltorio: es lo primero que se usa. La
          explicación de que se puede crear desde acá va debajo y en gris; era
          un renglón de ayuda metido en una caja con título, y la caja pesaba
          más que la ayuda. */}
      <div className="flex flex-col gap-1.5">
        <ClientPicker organizationId={orgId} value={selected} onChange={setSelected} />
        <p className="text-xs text-[var(--tinta-suave)]">
          Si el cliente no existe, se crea desde acá sin perder lo que estés cargando.
        </p>
      </div>

      {error && (
        <p role="alert" className="text-sm text-[var(--peligro)]" data-testid="clients-error">
          {error}
        </p>
      )}

      {selected && (
        <section className="superficie p-4" data-testid="client-detail">
          <h2 className="mb-1 font-medium">{selected.name}</h2>

          {/* El reparto habitual del cliente pisa al de la organización y lo
              pisa, a su vez, lo que se elija en el trabajo. Está acá y no en
              Configuración porque es una característica DE ESTE cliente: «con
              los Rombola siempre vamos a medias» es algo que se sabe mirando la
              ficha del cliente, no una lista de plantillas. */}
          <label className="mb-3 flex flex-col gap-1 text-sm">
            Reparto habitual con este cliente
            <select
              value={selected.default_split_template_id ?? ''}
              onChange={(e) => void cambiarPlantilla(e.target.value || null)}
              className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2"
              data-testid="client-split-template"
            >
              <option value="">Usar el de la organización</option>
              {plantillas.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <span className="text-xs text-[var(--tinta-tenue)]">
              Se aplica solo al crear un trabajo, y se puede cambiar en ese trabajo.
            </span>
          </label>

          {/* A nombre de quién se emite la factura de este cliente.
              Existe porque hay clientes que son dos personas —un campo de un
              matrimonio, una sucesión— y sin esto se le termina facturando el
              100 % a uno de los dos sin querer. No se resuelve duplicando el
              cliente: sería el mismo campo dos veces en todas las listas. */}
          <div className="mb-4 flex flex-col gap-2 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] p-3">
            <p className="text-sm font-medium">Destinatarios de facturación</p>
            <RepartoFactura lineas={destinatarios} onChange={setDestinatarios} />
            <button
              type="button"
              onClick={() => void guardarDestinatarios()}
              className="min-h-11 self-start rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-4 text-sm"
              data-testid="guardar-destinatarios"
            >
              Guardar destinatarios
            </button>
          </div>

          <p className="mb-3 text-sm text-[var(--tinta-suave)]">Establecimientos</p>

          <div className="flex flex-col gap-3">
            {establishments.length === 0 && (
              <p className="text-sm text-[var(--tinta-suave)]">
                Todavía no tiene establecimientos.
              </p>
            )}
            {establishments.map((e) => (
              <div key={e.id} className="flex flex-col gap-2">
                <EstablishmentEditor
                  establishment={e}
                  settings={settings}
                  participants={participants}
                  onSaved={() => setRecarga((n) => n + 1)}
                />
                <button
                  type="button"
                  onClick={() => setBorrandoCampo(e)}
                  className="min-h-11 self-start text-sm text-[var(--peligro)] underline "
                  data-testid="delete-establishment"
                >
                  Borrar campo
                </button>
              </div>
            ))}
          </div>

          <div className="mt-4 flex gap-2">
            <input
              type="text"
              value={nuevoCampo}
              onChange={(ev) => setNuevoCampo(ev.target.value)}
              placeholder="Nombre del campo"
              className="min-h-11 flex-1 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
              data-testid="new-establishment-name"
            />
            <button
              type="button"
              onClick={() => void agregarCampo()}
              className="min-h-11 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 text-sm text-[var(--sobre-accion)]"
              data-testid="add-establishment"
            >
              Agregar
            </button>
          </div>
        </section>
      )}

      <Seccion
        titulo="Todos los clientes"
        nota={
          // «0 en total» sin conexión era mentir con un número: no hay cero
          // clientes, no se pudo consultar. Se dice qué se está mirando.
          sinServidor && !bundle
            ? 'Sin conexión y todavía no hay clientes descargados en este dispositivo. Conectate una vez para prepararlo para uso offline.'
            : sinServidor
              ? `${clients.length} guardados en este teléfono · sin conexión`
              : `${clients.length} en total`
        }
      >
        <ul className="flex flex-col divide-y divide-[var(--linea)]" data-testid="clients-list">
          {clients.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-2 py-1">
              <button
                type="button"
                onClick={() => setSelected(c)}
                className="min-h-11 min-w-0 flex-1 py-1 text-left"
                data-testid="client-row"
              >
                <span
                  className={`block truncate font-medium ${
                    c.is_active ? '' : 'text-[var(--tinta-tenue)]'
                  }`}
                >
                  {c.name}
                  {!c.is_active && <span className="ml-2 text-xs">(inactivo)</span>}
                  {c.record_kind === 'legacy_placeholder' && (
                    <span className="ml-2 rounded-full bg-[var(--aviso-fondo)] px-2 py-0.5 text-xs text-[var(--aviso)]">
                      legacy
                    </span>
                  )}
                </span>
                {/* Los campos, como texto secundario. Son lo que distingue a
                    dos clientes con nombre parecido, y estaban sólo adentro
                    del detalle. */}
                {(campos[c.id]?.length ?? 0) > 0 && (
                  <span className="block truncate text-sm text-[var(--tinta-suave)]">
                    {campos[c.id]!.map((e) => e.name).join(' · ')}
                  </span>
                )}
              </button>

              {/* Un menú, no dos botones por renglón. Con setenta clientes
                  eran ciento cuarenta botones repetidos, y encontrar el
                  cliente costaba más que hacerle algo. */}
              <MenuAcciones
                etiqueta={`Acciones de ${c.name}`}
                testId="client-menu"
                acciones={[
                  {
                    etiqueta: 'Ver y editar',
                    testId: 'ver-cliente',
                    onClick: () => setSelected(c),
                  },
                  {
                    etiqueta: c.is_active ? 'Desactivar' : 'Reactivar',
                    testId: 'toggle-active',
                    onClick: () => void alternarActivo(c),
                  },
                  {
                    etiqueta: 'Eliminar',
                    testId: 'delete-client',
                    destructiva: true,
                    onClick: () => setBorrandoCliente(c),
                  },
                ]}
              />
            </li>
          ))}
        </ul>

        <p className="text-xs text-[var(--tinta-tenue)]">
          Desactivar y borrar son cosas distintas: desactivar saca al cliente del buscador y le deja
          los trabajos; borrar lo hace desaparecer, y sólo se puede si no tiene ninguno.
        </p>
      </Seccion>
      {/* Fuera del flujo a propósito: son `fixed`. Da igual dónde estén en el
          árbol, y el scroll de la lista no se mueve ni al abrirlas ni al
          cerrarlas. */}
      {borrandoCliente && (
        <ConfirmarBorrado
          que={`el cliente «${borrandoCliente.name}»`}
          detalle="Si tiene trabajos cargados, la base te va a decir cuántos y no se borra nada."
          ocupado={false}
          onCancelar={() => setBorrandoCliente(null)}
          onConfirmar={() => void borrarCliente(borrandoCliente)}
          testId="confirmar-borrar-cliente"
        />
      )}
      {borrandoCampo && (
        <ConfirmarBorrado
          que={`el campo «${borrandoCampo.name}»`}
          detalle="Si tiene trabajos cargados, la base te va a decir cuántos y no se borra nada."
          onCancelar={() => setBorrandoCampo(null)}
          onConfirmar={() => void borrarCampo(borrandoCampo)}
          testId="confirmar-borrar-campo"
        />
      )}
      <AvisoFlotante texto={aviso} onCerrar={() => setAviso(null)} />
    </div>
  );
}
